import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, ModalBody } from "reactstrap";

import { withTranslation } from "react-i18next";
import crossIcon from "assets/images/cross.svg";
import styles from "accountOwner/pages/Notes/Notes.module.scss";
import Button from "components/Button";
import editIcon from "assets/images/edit-icon.svg";
import Text from "components/Text";
import moment from "moment";
import {
  notifyAttendeesOptions,
  notifyAttendeesOptionsForAllDayEvent,
} from "../../../../constants";
import Empty from "components/Empty";
import { addEditEventsConst } from "accountOwner/pages/AddEvent/addEvents.constants";
import { displayCustomMessage } from "accountOwner/pages/AddEvent/addEvents.functions";
const LabelText = ({ heading, text }) => {
  return (
    <div className={styles.edit_icon_text}>
      <img src={editIcon} alt="edit" id="TooltipEditBtn" />
      <div className="d-flex flex-column justify-content-center">
        <Text
          size="12px"
          weight="400"
          color="#6F7788"
          lineHeight="12px"
          marginBottom="10px"
        >
          {heading}
        </Text>
        {typeof text !== "string" ? (
          text
        ) : (
          <Text size="14px" weight="600" color="#102C42" lineHeight="14px" wordBreak="break-all">
            {text}
          </Text>
        )}
      </div>
    </div>
  );
};
const EditChangesModal = ({
  t,
  isModalOpen,
  closeModal,
  detail,
  isShift,
  isBusySlot,
}) => {
  const divRef = useRef(null);
  const [showEmpty, setShowEmpty] = useState(false);
  useEffect(() => {
    const time = setTimeout(() => {
      if (!divRef.current.childElementCount) {
        setShowEmpty(true);
      }
    }, 0);
    return () => {
      clearTimeout(time);
    };
  }, []);
  const key = isBusySlot
    ? "schedulerBusySlotEditHistory"
    : "schedulerEventEditHistory";
  const value = detail[key];
  const repeatTypeMessage = useMemo(() => {
    if (
      !value?.customRepeatPattern ||
      value?.repeatedType !== addEditEventsConst.repeatedOptionsValues.custom
    )
      return null;
    return displayCustomMessage(
      {
        customRepeatPattern: value?.customRepeatPattern,
        isShift,
      },
      isBusySlot
    );
  }, [value?.customRepeatPattern]);
  if (!value) return null;

  const {
    endTime,
    isEndTimeEdit,
    isLocationEdit,
    isNoteEdit,
    isReminderBeforeInMinsEdit,
    isStartTimeEdit,
    isTitleEdit,
    location,
    note,
    reminderBeforeInMins,
    startTime,
    title,
    eventTags,
    eventRoles,
    isAllDayEventEdit,
    isBlockForPatientChange,
    blockForPatient,
    repeatedEndDate,
    isRepeatedEndDateChange,
    isEventDateChange,
    isRepeatTypeChange,
    repeatedType,
    eventDate,
    busySlotDate,
    isBusySlotDateChange
  } = value;
  const employeesList = detail?.invitees?.reduce(
    (prev, curr) => {
      const name =
        curr.employee.user.fullName ||
        curr.employee.user.firstName +
          " " +
          (curr.employee.user.lastName || "");
      if (curr.isAdded) {
        prev.added.push(name);
      } else {
        prev.removed.push(name);
      }
      return prev;
    },
    {
      added: [],
      removed: [],
    }
  );

  const officeList = detail?.offices?.reduce(
    (prev, curr) => {
      const name = curr.offices.office.name 
      if (curr.isAdded) {
        prev.added.push(name);
      } else {
        prev.removed.push(name);
      }
      return prev;
    },
    {
      added: [],
      removed: [],
    }
  );

  let list = !isBusySlot ? employeesList : officeList
  return (
    <Modal
      isOpen={isModalOpen}
      toggle={closeModal}
      className="modal-lg modal-dialog-centered"
      modalClassName="custom-modal"
    >
      <span className="close-btn" onClick={closeModal}>
        <img src={crossIcon} alt="close" />
      </span>
      <ModalBody>
        <div className={styles.alert_popup_content_wrapper}>
          <h2 className="page-title  mt-0">
            {t("scheduler.eventHistoryModalHeading")}
          </h2>
          {showEmpty && (
            <Empty Message={t("scheduler.eventHistoryModalEmptyMessage")} />
          )}
          <div className={styles.edit_change_popup_wrapper} ref={divRef}>
            {isTitleEdit && (
              <LabelText
                text={title}
                heading={t("scheduler.eventHistoryModalTitleHeading")}
              />
            )}
            {list?.added?.length > 0 && (
              <LabelText
                text={list?.added.join(", ")}
                heading={isBusySlot ? t("scheduler.busySlotHistoryModalNewMemberHeading")  :t("scheduler.eventHistoryModalNewMemberHeading")}
              />
            )}
            {list?.removed?.length > 0 && (
              <LabelText
                text={list?.removed.join(", ")}
                heading={isBusySlot ? t("scheduler.busySlotHistoryModalRemovedMemberHeading") : t("scheduler.eventHistoryModalRemovedMemberHeading")}
              />
            )}
            {isRepeatTypeChange && (
              <LabelText
                text={
                  addEditEventsConst
                    .repeatedOptions(t)
                    .find((res) => res.id === repeatedType)?.name || ""
                }
                heading={t("scheduler.eventHistoryModalRepeatTypeHeading")}
              />
            )}
            {isRepeatTypeChange &&
              repeatedType ===
                addEditEventsConst.repeatedOptionsValues.custom && (
                <LabelText
                  text={repeatTypeMessage || ""}
                  heading={t(
                    "scheduler.eventHistoryModalRepeatTypeMessageHeading"
                  )}
                />
              )}
            {isEventDateChange && eventDate && (
              <LabelText
                text={moment(eventDate).format("ll")}
                heading={t("scheduler.eventHistoryModalStartDateHeading")}
              />
            )}
            {isBusySlotDateChange && busySlotDate && (
              <LabelText
                text={moment(busySlotDate).format("ll")}
                heading={t("scheduler.eventHistoryModalStartDateHeading")}
              />
            )}
            {isRepeatedEndDateChange && repeatedEndDate && (
              <LabelText
                text={moment(repeatedEndDate).format("ll")}
                heading={t("scheduler.eventHistoryModalEndDateHeading")}
              />
            )}
            {(isEndTimeEdit || isStartTimeEdit || isAllDayEventEdit) && (
              <LabelText
                text={
                  (startTime
                    ? moment(startTime, "YYYY-MM-DDTHH:mm:ss").format("h:mm A")
                    : "") +
                  (endTime
                    ? " to " +
                      moment(endTime, "YYYY-MM-DDTHH:mm:ss").format("h:mm A")
                    : "")
                }
                heading={t("scheduler.eventHistoryModalStartEndTimeHeading")}
              />
            )}
            {isLocationEdit && (
              <LabelText
                text={location}
                heading={t("scheduler.eventHistoryModalLocationHeading")}
              />
            )}
            {eventRoles?.length > 0 && (
              <LabelText
                text={eventRoles.join(", ")}
                heading={t("scheduler.eventHistoryModalRolesHeading")}
              />
            )}
            {isBlockForPatientChange && (
              <LabelText
                text={`${blockForPatient}`}
                heading={t("scheduler.eventHistoryModalBlockHeading")}
              />
            )}
            {isNoteEdit && (
              <LabelText
                text={note}
                heading={t("scheduler.eventHistoryModalNotesHeading")}
              />
            )}
            {eventTags?.length > 0 && (
              <LabelText
                text={
                  <div className={styles["tag-list"]}>
                    {eventTags?.map((v, index) => (
                      <span key={v + index}>{v}</span>
                    ))}
                  </div>
                }
                heading={t("scheduler.eventHistoryModalTagsHeading")}
              />
            )}
            {isReminderBeforeInMinsEdit && (
              <LabelText
                text={
                  [
                    ...notifyAttendeesOptionsForAllDayEvent(isShift),
                    ...notifyAttendeesOptions,
                  ].find((option) => option.id === reminderBeforeInMins)
                    ?.name || ""
                }
                heading={t("scheduler.eventHistoryModalNotifyHeading")}
              />
            )}
          </div>
          <div className={styles.main_btn_container}>
            <Button onClick={closeModal}>{t("close")}</Button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};
export default withTranslation()(EditChangesModal);
